import { useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { doc, updateDoc } from "firebase/firestore"
import { toast } from "react-toastify"
import { Box, Typography } from "@mui/material"
import { db } from "../../firebase"
import { parseSquareCallback } from "./lib/square"

// Square Point of Sale API の Web Callback URL に固定で登録する画面。
// Square Developer Dashboard側の「Web Callback URL」は固定URLしか登録できないため、
// 出荷IDはURLパスではなく state パラメータで受け取り、対象の出荷詳細画面へ転送する。
const ShipmentSquareCallback = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // 同じ画面インスタンスのまま複数回コールバックを受ける可能性があるため、
  // 「一度でも処理したら二度と処理しない」ではなく「同じsearch文字列は二重処理しない」形にする
  const lastHandledSearch = useRef<string | null>(null)

  useEffect(() => {
    if (lastHandledSearch.current === location.search) return
    lastHandledSearch.current = location.search

    const result = parseSquareCallback(location.search)
    const shipmentId = result.state

    if (!shipmentId) {
      toast.error("Square決済の呼び出し元（出荷情報）が特定できませんでした")
      navigate('/admin/shipments', { replace: true })
      return
    }

    if (result.status === 'success') {
      updateDoc(doc(db, 'shipments', shipmentId), {
        squarePaymentStatus: 'completed',
        squareTransactionId: result.transactionId || null,
        squarePaidAt: new Date().toISOString(),
      }).then(() => {
        toast.success('Square決済が完了しました')
      }).catch((e) => {
        toast.error('決済結果の保存に失敗しました: ' + JSON.stringify(e))
      }).finally(() => {
        navigate(`/admin/shipments/${shipmentId}`, { replace: true })
      })
    } else {
      // Squareアプリ側の応答が読み取れなかった／エラーコードが返ってきたケース。
      // 生のcallback文字列を残しておくと、再発時に原因（エラーコードの種類）を追跡しやすい。
      console.error('Square POS callback failed', { search: location.search, result })
      updateDoc(doc(db, 'shipments', shipmentId), {
        squarePaymentStatus: 'failed',
        squarePaymentErrorCode: result.errorCode || (result.status === 'none' ? 'NO_RESPONSE_DATA' : null),
        squarePaymentFailedAt: new Date().toISOString(),
      }).catch(() => { /* 失敗記録の保存エラーはユーザー体験に影響させない */ })

      const guidance = result.status === 'none'
        ? '（Squareアプリからの応答を受信できませんでした。Point of Saleアプリを一度完全に終了してから、もう一度「Squareで決済」をお試しください）'
        : ''
      toast.error(`Square決済が完了しませんでした${result.errorCode ? `（${result.errorCode}）` : ''}${guidance}`, { autoClose: 10000 })
      navigate(`/admin/shipments/${shipmentId}`, { replace: true })
    }
  }, [location.search, navigate])

  return (
    <Box m={4}>
      <Typography>Square決済の結果を処理しています...</Typography>
    </Box>
  )
}

export default ShipmentSquareCallback
